import { useEffect, useRef, useState } from "react";

// Thin wrapper over the browser Web Speech API (Chrome / Android WebView / Capacitor).
// One active recognition instance per hook call. Supports en-IN, ta-IN, etc.
//
// Usage:
//   const { listening, error, supported, start, stop } = useSpeechToText();
//   start({ lang: "ta-IN", onResult: (text) => ... });
export default function useSpeechToText() {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null); // 'not_supported' | 'permission_denied' | 'no_speech' | 'error' | null
  const recognitionRef = useRef(null);
  const onResultRef = useRef(null);

  const SR =
    typeof window !== "undefined"
      ? window.SpeechRecognition || window.webkitSpeechRecognition
      : null;
  const supported = !!SR;

  const start = ({ lang = "en-IN", onResult } = {}) => {
    if (!supported) { setError("not_supported"); return; }
    // Stop any in-flight recognition first.
    try { recognitionRef.current?.abort(); } catch (_e) { /* ignore */ }

    setError(null);
    onResultRef.current = onResult;

    const r = new SR();
    r.lang = lang;
    r.interimResults = false;
    r.maxAlternatives = 1;
    r.continuous = false;

    r.onresult = (e) => {
      const transcript = e.results?.[0]?.[0]?.transcript || "";
      if (transcript && onResultRef.current) onResultRef.current(transcript.trim());
    };
    r.onerror = (e) => {
      const code = e?.error;
      if (code === "not-allowed" || code === "service-not-allowed") setError("permission_denied");
      else if (code === "no-speech") setError("no_speech");
      else if (code === "aborted") setError(null);
      else setError("error");
    };
    r.onend = () => setListening(false);

    recognitionRef.current = r;
    try {
      r.start();
      setListening(true);
    } catch (_e) {
      // start() throws on Chrome if already started — surface as a soft error.
      setError("error");
      setListening(false);
    }
  };

  const stop = () => {
    try { recognitionRef.current?.stop(); } catch (_e) { /* ignore */ }
    setListening(false);
  };

  useEffect(
    () => () => { try { recognitionRef.current?.abort(); } catch (_e) { /* ignore */ } },
    []
  );

  return { listening, error, supported, start, stop };
}
